import {ArrowLeftIcon, ArrowRightIcon} from "@heroicons/react/20/solid";
import type {AuditScoreType} from "@ots-frontend/ots-data";
import {AuditScoreRing} from "../../audit/AuditScoreRing";
import {OtsButton} from "../../components/OtsButton";
import {OtsTooltip} from "../../components/OtsTooltip";
import {containerTicketId, type ContainerTicketParty, haulerNameOf} from "../../container-status/container-details-model";
import {ActivityAttachmentsBadge} from "../../driver-activity/ActivityAttachmentsBadge";
import {ActivityEntryPreview} from "../../driver-activity/ActivityEntryPreview";
import {ActivityGeofenceChip} from "../../driver-activity/ActivityGeofenceChip";
import {ActivityStatusIcon} from "../../driver-activity/ActivityStatusIcon";
import {formatActivityDateTime, formatActivityQuantity} from "../../driver-activity/driver-activity-format";
import type {ActivityEntry} from "../../driver-activity/model/activity-types";
import {plainMapEntity, type RenderMapEntity} from "./map-panel";
import {siteTicketLeg} from "./site-tickets";

export type SiteTicketListProps = {
  entries: readonly ActivityEntry[];
  /** The site the panel is about; a ticket delivered here reads "from" its pickup rather than "to" its delivery. */
  siteId: string;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  timeZone?: string;
  auditScoreType?: AuditScoreType;
  /** Whether each row names the hauler (HubGo) or the driver (DispatchGo). */
  party?: ContainerTicketParty;
  /** Draws the container's name; the apps link it to the container. */
  renderEntity?: RenderMapEntity;
  /** Opens the ticket's profile from its ID. */
  onOpenTicket?: (entry: ActivityEntry) => void;
  onOpenAudit?: (entry: ActivityEntry) => void;
  onOpenAttachments?: (entry: ActivityEntry) => void;
};

const idClasses = "truncate rounded font-mono text-[13px] font-semibold text-[var(--ots-primary)] outline-none focus-visible:ring-2 focus-visible:ring-[var(--ots-primary)]";

/**
 * A site's latest tickets as compact two-line rows, built like the container panel's: status icon, ticket ID with its
 * preview card, time, geofence time on site, quantity, audit ring and attachments, then the tank here, who hauled it,
 * and the other end of the haul.
 */
export const SiteTicketList = ({
  entries,
  siteId,
  loading,
  error,
  onRetry,
  timeZone,
  auditScoreType = "BUSINESS_VALIDATION_CONFIDENCE",
  party = "driver",
  renderEntity = plainMapEntity,
  onOpenTicket,
  onOpenAudit,
  onOpenAttachments,
}: SiteTicketListProps) => {
  if (loading && !entries.length) {
    return <p className="!m-0 text-xs text-[var(--ots-text-muted)]">Loading…</p>;
  }
  if (error && !entries.length) {
    return <div className="flex items-center gap-2 text-xs text-[var(--ots-error)]">
      <span>Couldn't load tickets. {error}</span>
      {onRetry && <OtsButton variant="text" size="mini" onClick={onRetry}>Retry</OtsButton>}
    </div>;
  }
  if (!entries.length) {
    return <p className="!m-0 text-xs text-[var(--ots-text-muted)]">No tickets in this time.</p>;
  }
  return (
    <ul aria-label="Recent tickets" className="!m-0 !p-0 list-none divide-y divide-[var(--ots-outline-variant)]">
      {entries.map((entry) => {
        const {outbound, here, there} = siteTicketLeg(entry, siteId);
        const Direction = outbound ? ArrowRightIcon : ArrowLeftIcon;
        const id = containerTicketId(entry) ?? "—";
        const geofence = outbound ? entry.pickupGeofence : entry.deliveryGeofence;
        const name = party === "hauler" ? haulerNameOf(here) ?? "Unknown hauler" : here?.personName ?? "Unknown driver";
        return (
          <li
            key={entry.key}
            data-ticket={entry.key}
            className="grid grid-cols-[auto_minmax(0,1fr)_auto] items-center gap-x-2 gap-y-0.5 px-1 py-2 text-xs">
            <span className="row-span-2 flex items-center"><ActivityStatusIcon entry={entry}/></span>
            <span className="flex min-w-0 items-center gap-2">
              <OtsTooltip panelClassName="!max-w-none !p-3" placement="bottom"
                content={<ActivityEntryPreview entry={entry} timeZone={timeZone} auditScoreType={auditScoreType}/>}>
                {onOpenTicket && entry.tta
                  ? <button type="button" className={[idClasses, "hover:underline"].join(" ")} onClick={() => onOpenTicket(entry)}>{id}</button>
                  : <span tabIndex={0} className={idClasses}>{id}</span>}
              </OtsTooltip>
              <span className="shrink-0 tabular-nums text-[var(--ots-text-muted)]">{formatActivityDateTime(entry.time, timeZone)}</span>
              <ActivityGeofenceChip match={geofence} ticketTime={here?.createDttm} timeZone={timeZone}/>
            </span>
            <span className="flex items-center justify-end gap-2">
              <span className="font-semibold tabular-nums">{formatActivityQuantity(here?.quantityDecimal, here?.quantityUnitOfMeasure)}</span>
              {entry.tta && <AuditScoreRing audit={entry.audit} scoreType={auditScoreType} timeZone={timeZone} onClick={onOpenAudit && (() => onOpenAudit(entry))}/>}
            </span>
            <span className="flex min-w-0 items-center gap-1.5 text-[var(--ots-text-muted)]">
              {here?.containerName && (
                <span
                  data-container={here.containerId}
                  title="Container"
                  className="shrink-0 rounded bg-[var(--ots-surface-variant)] px-1.5 py-px text-[11px] font-medium text-[var(--ots-text)]">
                  {renderEntity({id: here.containerId, name: here.containerName, type: "CONTAINER"})}
                </span>
              )}
              <span className="truncate">{name}</span>
              <Direction aria-label={outbound ? "to" : "from"} className="size-3 shrink-0"/>
              <span className="truncate">{there?.siteName ?? (outbound ? "Not delivered yet" : "Unknown pickup")}</span>
            </span>
            <span className="flex justify-end">
              <ActivityAttachmentsBadge attachments={entry.attachments} onOpen={onOpenAttachments && (() => onOpenAttachments(entry))}/>
            </span>
          </li>
        );
      })}
    </ul>
  );
};
